import React from 'react';
import { FiUpload, FiUsers, FiX, FiDownload, FiCloud } from 'react-icons/fi';
import { useEditing } from '../EditingContext';
import StartDate from '../StartDate';
import VisibleWeeks from '../VisibleWeeks';
import Zoom from '../Zoom';
import useLoadPlans from '../hooks/useLoadPlans';
import useLoadPublishedPlans from '../hooks/useLoadPublishedPlans';
import { deletePlan, fetchPlanVersions, fetchPublishedPlanVersions } from '../api';
import { useConfirmAlert } from '../hooks/useConfirmAlert';
import PlanTabs from './PlanTabs';
import StudentPlans from './StudentPlans';

const Sidebar = ({ 
  width, 
  minWidth,
  maxWidth, 
  left, 
  visibleWeeks, 
  setVisibleWeeks, 
  onVisibleWeeksChangeStart,
  onVisibleWeeksChangeEnd,
  weekWidth,
  setWeekWidth,
  onZoomChangeStart,
  onZoomChangeEnd,
  hasOpenInfoPanels,
  onCloseAllInfoPanels,
  onLoad, 
  onSave,
  onDatabaseSave,
  onLoadFromDatabase,
  onUpdatePlan, 
  user, // <-- user als Prop
  onSchuelerOpen,
  onCreateNewPlan,
  currentLoadedPlanId, // <-- ID des aktuell geladenen Plans
  batchOperations, // <-- Batch Operations Hook
  onLoadStudentPlan, // <-- Callback für das Laden von Lernenden-Plänen
  startDate
}) => {
  const { showEditButtons, canEdit, isLearner } = useEditing();
  const { plans, loading, error, refresh } = useLoadPlans(user);
  const { publishedPlans, loading: loadingPublished, error: errorPublished, refresh: refreshPublished } = useLoadPublishedPlans(user);
  const { confirm, alert } = useConfirmAlert();
  const [expandedPlans, setExpandedPlans] = React.useState(new Set());
  const [planVersions, setPlanVersions] = React.useState({});
  const [loadingVersions, setLoadingVersions] = React.useState(new Set());
  const [deleteDropdownOpen, setDeleteDropdownOpen] = React.useState(null);
  
  // Separate state for published plans
  const [expandedPublishedPlans, setExpandedPublishedPlans] = React.useState(new Set());
  const [publishedPlanVersions, setPublishedPlanVersions] = React.useState({});
  const [loadingPublishedVersions, setLoadingPublishedVersions] = React.useState(new Set());
  
  // Plan-Versionen laden
  const loadPlanVersions = React.useCallback(async (baseName, force = false) => {
    if (!user?.username || (!force && planVersions[baseName])) {
      return;
    }

    setLoadingVersions(prev => new Set(prev).add(baseName));
    
    try {
      const result = await fetchPlanVersions(user.username, baseName);
      if (result.error) {
        console.error('Fehler beim Laden der Versionen:', result.error);
      } else if (result.success && result.versions) {
        setPlanVersions(prev => ({
          ...prev,
          [baseName]: result.versions
        }));
      }
    } catch (error) {
      console.error('Fehler beim Laden der Versionen:', error);
    } finally {
      setLoadingVersions(prev => {
        const next = new Set(prev);
        next.delete(baseName);
        return next;
      });
    }
  }, [user?.username, planVersions]);

  // Global refresh function für andere Komponenten
  React.useEffect(() => {
    window.refreshPlans = refresh;
    window.refreshPublishedPlans = refreshPublished;
    window.refreshPlanVersions = (baseName) => {
      if (baseName) {
        loadPlanVersions(baseName, true);
      }
    };
    return () => {
      delete window.refreshPlans;
      delete window.refreshPublishedPlans;
      delete window.refreshPlanVersions;
    };
  }, [refresh, refreshPublished, loadPlanVersions]);

  // Plan-Versionen laden für veröffentlichte Pläne
  const loadPublishedPlanVersions = async (planKey, planOwnerId, baseName, force = false) => {
    if (!user?.username || (!force && publishedPlanVersions[planKey])) {
      return;
    }

    setLoadingPublishedVersions(prev => new Set(prev).add(planKey));
    
    try {
      const result = await fetchPublishedPlanVersions(user.username, baseName, planOwnerId);
      if (result.error) {
        console.error('Fehler beim Laden der veröffentlichten Versionen:', result.error);
      } else if (result.success && result.versions) {
        setPublishedPlanVersions(prev => ({
          ...prev,
          [planKey]: result.versions
        }));
      }
    } catch (error) {
      console.error('Fehler beim Laden der veröffentlichten Versionen:', error);
    } finally {
      setLoadingPublishedVersions(prev => {
        const next = new Set(prev);
        next.delete(planKey);
        return next;
      });
    }
  };

  // Plan erweitern/zusammenklappen
  const togglePlanExpansion = (baseName) => {
    setExpandedPlans(prev => {
      const next = new Set(prev);
      if (next.has(baseName)) {
        next.delete(baseName);
      } else {
        next.add(baseName);
        loadPlanVersions(baseName);
      }
      return next;
    });
  };

  // Veröffentlichte Pläne erweitern/zusammenklappen
  const togglePublishedPlanExpansion = (planKey, planOwnerId, baseName) => {
    setExpandedPublishedPlans(prev => {
      const next = new Set(prev);
      if (next.has(planKey)) {
        next.delete(planKey);
      } else {
        next.add(planKey);
        loadPublishedPlanVersions(planKey, planOwnerId, baseName);
      }
      return next;
    });
  };

  // Plan löschen
  const handleDeletePlan = async (planId, planName) => {
    const confirmed = await confirm(`Möchten Sie den Plan "${planName}" wirklich löschen?`, {
      title: "Plan löschen",
      confirmText: "Löschen",
      cancelText: "Abbrechen"
    });
    
    if (!confirmed) {
      return;
    }

    // Finde den Plan um den baseName zu ermitteln - zuerst in der Hauptliste, dann in den Versionen
    let planToDelete = plans.find(plan => plan.id === planId);
    let baseName = planToDelete?.base_name;
    
    // Wenn nicht in der Hauptliste gefunden, in den Versionen suchen
    if (!baseName) {
      for (const [planBaseName, versions] of Object.entries(planVersions)) {
        const foundVersion = versions.find(version => version.id === planId);
        if (foundVersion) {
          baseName = planBaseName;
          planToDelete = foundVersion;
          break;
        }
      }
    }

    try {
      const result = await deletePlan(planId, user.username);
      if (result.error) {
        await alert('Fehler beim Löschen: ' + result.error, { title: "Fehler" });
      } else if (result.success) {
        await alert('Plan erfolgreich gelöscht!', { title: "Erfolg" });
        
        // Liste aktualisieren
        refresh();
        
        // Versionen für diesen Plan aktualisieren, falls sie geladen waren
        if (baseName && planVersions[baseName]) {
          if (expandedPlans.has(baseName)) {
            // Die gelöschte Version aus dem lokalen State entfernen
            setPlanVersions(prev => {
              const updated = { ...prev };
              if (updated[baseName]) {
                updated[baseName] = updated[baseName].filter(version => version.id !== planId);
                // Wenn keine Versionen mehr übrig sind, Plan aus expanded entfernen
                if (updated[baseName].length === 0) {
                  setExpandedPlans(expandedPrev => {
                    const next = new Set(expandedPrev);
                    next.delete(baseName);
                    return next;
                  });
                }
              }
              return updated;
            });
          } else {
            // Falls nicht expanded, Cache einfach löschen
            setPlanVersions(prev => {
              const updated = { ...prev };
              delete updated[baseName];
              return updated;
            });
          }
        }
        
        setDeleteDropdownOpen(null); // Dropdown schließen
      } else {
        await alert('Unerwartete Antwort vom Server', { title: "Fehler" });
      }
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      await alert('Verbindungsfehler beim Löschen des Plans', { title: "Verbindungsfehler" });
    }
  };

  // Alle Versionen eines Plans löschen
  const handleDeleteAllVersions = async (baseName) => {
    const confirmed = await confirm(
      `Möchten Sie wirklich ALLE Versionen von "${baseName}" löschen?\n\nDiese Aktion kann nicht rückgängig gemacht werden!`, 
      {
        title: "Alle Versionen löschen",
        confirmText: "Alle löschen",
        cancelText: "Abbrechen"
      }
    );
    
    if (!confirmed) {
      return;
    }

    try {
      const versions = planVersions[baseName] || [];
      let successCount = 0;
      let errorCount = 0;

      for (const version of versions) {
        const result = await deletePlan(version.id, user.username);
        if (result.success) {
          successCount++;
        } else {
          errorCount++;
        }
      }

      if (errorCount === 0) {
        await alert(`Alle ${successCount} Versionen erfolgreich gelöscht!`, { title: "Erfolg" });
      } else {
        await alert(`${successCount} Versionen gelöscht, ${errorCount} Fehler aufgetreten.`, { title: "Teilweise erfolgreich" });
      }
      
      // Liste aktualisieren
      refresh();
      
      // Versionen-Cache für diesen Plan löschen
      setPlanVersions(prev => {
        const updated = { ...prev };
        delete updated[baseName];
        return updated;
      });
      
      // Plan aus der expandedPlans entfernen, da keine Versionen mehr vorhanden
      setExpandedPlans(prev => {
        const next = new Set(prev);
        next.delete(baseName);
        return next;
      });
      
      setDeleteDropdownOpen(null); // Dropdown schließen
    } catch (error) {
      console.error('Fehler beim Löschen aller Versionen:', error);
      await alert('Verbindungsfehler beim Löschen der Versionen', { title: "Verbindungsfehler" });
    }
  };

  // Nur aktuelle Version behalten (alle älteren Versionen löschen)
  const handleDeleteOlderVersions = async (baseName, currentPlanId) => {
    const confirmed = await confirm(
      `Möchten Sie alle älteren Versionen von "${baseName}" löschen und nur die aktuelle Version behalten?\n\nDiese Aktion kann nicht rückgängig gemacht werden!`, 
      {
        title: "Ältere Versionen löschen",
        confirmText: "Ältere löschen",
        cancelText: "Abbrechen"
      }
    );
    
    if (!confirmed) {
      return;
    }

    try {
      const versions = planVersions[baseName] || [];
      // Nur Versionen löschen, die nicht die aktuelle sind
      const versionsToDelete = versions.filter(version => version.id !== currentPlanId);
      
      let successCount = 0;
      let errorCount = 0;

      for (const version of versionsToDelete) {
        const result = await deletePlan(version.id, user.username);
        if (result.success) {
          successCount++;
        } else {
          errorCount++;
        }
      }

      if (errorCount === 0 && successCount > 0) {
        await alert(`${successCount} ältere Version(en) erfolgreich gelöscht!`, { title: "Erfolg" });
      } else if (successCount === 0) {
        await alert('Keine älteren Versionen zum Löschen gefunden.', { title: "Information" });
      } else {
        await alert(`${successCount} Versionen gelöscht, ${errorCount} Fehler aufgetreten.`, { title: "Teilweise erfolgreich" });
      }
      
      // Liste aktualisieren
      refresh();
      
      // Versionen für diesen Plan aktualisieren - nur die aktuelle Version behalten
      setPlanVersions(prev => {
        const updated = { ...prev };
        if (updated[baseName]) {
          updated[baseName] = updated[baseName].filter(version => version.id === currentPlanId);
        }
        return updated;
      });
      
      setDeleteDropdownOpen(null); // Dropdown schließen
    } catch (error) {
      console.error('Fehler beim Löschen älterer Versionen:', error);
      await alert('Verbindungsfehler beim Löschen der Versionen', { title: "Verbindungsfehler" });
    }
  };

  // Toggle Delete Dropdown
  const toggleDeleteDropdown = (planBaseName) => {
    if (deleteDropdownOpen === planBaseName) {
      setDeleteDropdownOpen(null);
    } else {
      setDeleteDropdownOpen(planBaseName);
      // Versionen laden falls noch nicht geladen
      if (!planVersions[planBaseName]) {
        loadPlanVersions(planBaseName);
      }
    }
  };

  // Dropdown schließen bei Klick außerhalb
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (deleteDropdownOpen && !event.target.closest('.delete-dropdown-container')) {
        setDeleteDropdownOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [deleteDropdownOpen]);

  return (
    <div style={{
      width: width,
      minWidth: minWidth,
      maxWidth: maxWidth,
      background: '#f4f4f4',
      overflowY: 'auto',
      overflowX: 'visible',
      marginTop: 8,
    }}>
      <div style={{
        borderBottom: '1px solid #ccc', 
        padding: 0,  
        alignItems: 'center', 
        gap: 8, 
        height: 30
      }}>
        {left || 'Linke Spalte'}
      </div>



      <div style={{ padding: 5 }}>
        <StartDate />
        
        {/* Close All InfoPanels Button - nur anzeigen wenn InfoPanels geöffnet sind */}
        {hasOpenInfoPanels && (
          <div style={{ marginTop: 12, marginBottom: 8 }}>
            <button
              onClick={onCloseAllInfoPanels}
              style={{
                backgroundColor: '#ff9800',
                color: 'white',
                border: 'none',
                padding: '8px 12px',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px',
                fontWeight: '500',
                width: '100%',
                justifyContent: 'center'
              }}
              title="Alle geöffneten TaskInfoPanels schließen"
            >
              <FiX />
              Alle InfoPanels schließen
            </button>
          </div>
        )}


        
        <VisibleWeeks 
          value={visibleWeeks} 
          onChange={setVisibleWeeks}
          onChangeStart={onVisibleWeeksChangeStart}
          onChangeEnd={onVisibleWeeksChangeEnd}
          disabled={hasOpenInfoPanels}
          startDate={startDate}
        />
        
        <Zoom 
          value={weekWidth} 
          onChange={setWeekWidth}
          onChangeStart={onZoomChangeStart}
          onChangeEnd={onZoomChangeEnd}
          disabled={hasOpenInfoPanels}
        />
        
        {/* Lernenden-Info - nur für Lernende sichtbar */}
        {isLearner && (
          <div style={{ 
            marginTop: 16, 
            padding: '12px', 
            backgroundColor: '#e3f2fd', 
            borderRadius: '6px', 
            border: '1px solid #2196f3',
            textAlign: 'center'
          }}>
            <div style={{ 
              fontSize: '14px', 
              fontWeight: '500', 
              color: '#1976d2',
              marginBottom: '4px' 
            }}>
              📚 Lernenden-Ansicht
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: '#424242',
              lineHeight: '1.4' 
            }}>
              Sie können den Lehrplan ansehen und navigieren. 
              Verwenden Sie die Bedienelemente um die Ansicht anzupassen.
            </div>
          </div>
        )}
        
        {/* Schüler Button - oberhalb des Laden Buttons */}
        {false && canEdit && (
        <div style={{ marginTop: 12 }}>
          <button
            style={{
              backgroundColor: '#9c27b0',
              color: 'white',
              border: 'none',
              padding: '8px 12px',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '14px',
              fontWeight: '500',
              width: '100%',
              justifyContent: 'center'
            }}
            onClick={onSchuelerOpen}
          >
            <FiUsers />
            Filter
          </button>
        </div>
        )}
        
        {/* Cloud speichern - Hauptbutton nur wenn User angemeldet und KEIN Lernender */}
            {user && !isLearner && (
              <div style={{ marginTop: 8 }}>
                <button
                  style={{
                    backgroundColor: '#4caf50',
                    color: 'white',
                    border: 'none',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    width: '100%',
                    justifyContent: 'center',
                    boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)'
                  }}
                  onClick={onDatabaseSave}
                  title="In der Cloud speichern"
                >
                  <FiCloud />
                  Speichern als
                </button>
              </div>
            )}

        
        {/* Plan Tabs - erscheint wenn Benutzer angemeldet ist und KEIN Lernender */}
        {!isLearner && (
        <PlanTabs
          user={user}
          // Props für MeinePlaene
          plans={plans}
          loading={loading}
          error={error}
          refresh={refresh}
          expandedPlans={expandedPlans}
          setExpandedPlans={setExpandedPlans}
          planVersions={planVersions}
          setPlanVersions={setPlanVersions}
          loadingVersions={loadingVersions}
          setLoadingVersions={setLoadingVersions}
          deleteDropdownOpen={deleteDropdownOpen}
          setDeleteDropdownOpen={setDeleteDropdownOpen}
          showEditButtons={showEditButtons}
          onLoadFromDatabase={onLoadFromDatabase}
          onUpdatePlan={onUpdatePlan}
          handleDeletePlan={handleDeletePlan}
          handleDeleteAllVersions={handleDeleteAllVersions}
          handleDeleteOlderVersions={handleDeleteOlderVersions}
          loadPlanVersions={loadPlanVersions}
          togglePlanExpansion={togglePlanExpansion}
          toggleDeleteDropdown={toggleDeleteDropdown}
          onCreateNewPlan={onCreateNewPlan}
          currentLoadedPlanId={currentLoadedPlanId}
          // Props für VeroeffentlichtePlaene
          publishedPlans={publishedPlans}
          loadingPublished={loadingPublished}
          errorPublished={errorPublished}
          refreshPublished={refreshPublished}
          expandedPublishedPlans={expandedPublishedPlans}
          setExpandedPublishedPlans={setExpandedPublishedPlans}
          publishedPlanVersions={publishedPlanVersions}
          setPublishedPlanVersions={setPublishedPlanVersions}
          loadingPublishedVersions={loadingPublishedVersions}
          setLoadingPublishedVersions={setLoadingPublishedVersions}
          loadPublishedPlanVersions={loadPublishedPlanVersions}
          togglePublishedPlanExpansion={togglePublishedPlanExpansion}
        />
        )}

        {/* Student Plans - nur für Lernende */}
        {isLearner && (
          <StudentPlans
            user={user}
            onLoadStudentPlan={onLoadStudentPlan}
          />
        )}

        {/* Datei-Laden Button - erscheint wenn Benutzer angemeldet ist und KEIN Lernender */}
        {!isLearner && (
          <div style={{ marginTop: 8 }}>
            <button
              style={{
                backgroundColor: '#757575',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px',
                fontWeight: 'normal',
                width: '100%',
                justifyContent: 'center',
                opacity: 0.8
              }}
              onClick={onLoad}
              title="Plan aus lokaler .syllabus Datei laden"
            >
              <FiUpload />
              lokales Backup laden
            </button>
          </div>
        )}

        {/* Speichern Buttons - nur wenn Edit-Buttons sichtbar sind */}
        {showEditButtons && (
          <>
            {/* Datei-Download - primärer Button */}
            
            {/* Datei-Download - sekundärer Button */}
            <div style={{ marginTop: user ? 6 : 8 }}>
              <button
                style={{
                  backgroundColor: user ? '#757575' : '#2196f3',
                  color: 'white',
                  border: 'none',
                  padding: user ? '6px 12px' : '8px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize:  '14px',
                  fontWeight: user ? 'normal' : '500',
                  width: '100%',
                  justifyContent: 'center',
                  opacity: user ? 0.8 : 1
                }}
                onClick={onSave}
                title={"Plan als .syllabus Datei herunterladen" }
              >
                <FiDownload />
                Backup lokal speichern
              </button>
            </div>
          </>
        )}

        
      </div>

      
    </div>
  );
};

export default Sidebar;
